/**
 * Mock schedule.
 *
 * SIMULATED DATA: every record carries `isSimulated: true` and the UI
 * badges it as a simulation wherever it appears (Implementation Guide §21).
 *
 * Kickoff times are computed relative to "now" so the demo schedule always
 * looks sensible, rather than drifting into the past as the repo ages.
 */

export const SEASON_TYPE = Object.freeze({
  PRESEASON: "preseason",
  REGULAR: "regular",
  PLAYOFFS: "playoffs",
  SUPER_BOWL: "superbowl",
});

export const SEASON_TYPE_LABELS = Object.freeze({
  preseason: "Preseason",
  regular: "Regular season",
  playoffs: "Playoffs",
  superbowl: "Super Bowl",
});

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

/** Set a date to a fixed local hour so cards don't show odd times. */
function atLocalHour(base, dayOffset, hour) {
  const date = new Date(base + dayOffset * DAY);
  date.setHours(hour, 0, 0, 0);
  return date.getTime();
}

const TEMPLATE = [
  {
    gameId: "mock-2026-pre-buf-car",
    awayTeamId: "BUF",
    homeTeamId: "CAR",
    dayOffset: 0,
    hour: 19,
    featured: true,
  },
  {
    gameId: "mock-2026-pre-kc-det",
    awayTeamId: "KC",
    homeTeamId: "DET",
    dayOffset: 1,
    hour: 13,
  },
  {
    gameId: "mock-2026-pre-phi-nyj",
    awayTeamId: "PHI",
    homeTeamId: "NYJ",
    dayOffset: 2,
    hour: 16,
  },
  {
    gameId: "mock-2026-pre-sea-sf",
    awayTeamId: "SEA",
    homeTeamId: "SF",
    dayOffset: 4,
    hour: 20,
  },
  {
    gameId: "mock-2026-pre-dal-lar",
    awayTeamId: "DAL",
    homeTeamId: "LAR",
    dayOffset: 6,
    hour: 18,
  },
];

/**
 * Beginner-friendly pre-game notes (PRD §14).
 *
 * These are position-level, not player-level. The MVP has no verified
 * roster source, so naming a "player to watch" would mean inventing one.
 */
const PRE_GAME_NOTES = [
  {
    watchFor: {
      title: "Watch the quarterback",
      body: "Every play starts with them. If they have time to throw, their team usually moves the ball.",
      conceptId: "quarterback",
    },
    matchup: {
      title: "The pass rush against the offensive line",
      body: "If the defenders get through quickly, you'll see sacks and short drives. If they don't, expect long passes.",
      conceptId: "sack",
    },
    lessonConceptId: "third-down",
  },
  {
    watchFor: {
      title: "Watch the running back",
      body: "Short, steady runs keep the clock moving and set up easier third downs.",
      conceptId: "running-back",
    },
    matchup: {
      title: "Runs against the defensive line",
      body: "If runs keep gaining 4 or 5 yards, the offence controls the game without ever throwing deep.",
      conceptId: "defensive-line",
    },
    lessonConceptId: "first-down",
  },
  {
    watchFor: {
      title: "Watch the wide receivers",
      body: "They line up near the sidelines. When one gets a step on his defender, that's when the big plays happen.",
      conceptId: "wide-receiver",
    },
    matchup: {
      title: "Receivers against defensive backs",
      body: "One-on-one down the sideline is the most exciting matchup on the field, and where interceptions come from.",
      conceptId: "defensive-back",
    },
    lessonConceptId: "interception",
  },
];

/**
 * @param {number} now epoch ms
 * @returns {object[]} game records, earliest kickoff first
 */
export function buildMockSchedule(now = Date.now()) {
  return TEMPLATE.map((entry, index) => ({
    gameId: entry.gameId,
    provider: "mock",
    isSimulated: true,

    season: new Date(now).getFullYear(),
    seasonType: SEASON_TYPE.PRESEASON,
    week: 1,

    awayTeamId: entry.awayTeamId,
    homeTeamId: entry.homeTeamId,
    kickoffAt: atLocalHour(now, entry.dayOffset, entry.hour),
    venue: null,

    featured: Boolean(entry.featured),
    hasSimulation: true,

    preGame: PRE_GAME_NOTES[index % PRE_GAME_NOTES.length],
  })).sort((a, b) => a.kickoffAt - b.kickoffAt);
}

export const FEATURED_MOCK_GAME_ID = "mock-2026-pre-buf-car";
